"use client";

import * as React from "react";
import { Info, Plus } from "lucide-react";

import { NotebookSwitcher } from "@/src/pages/study/components/layout/notebook-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Notebook } from "../../client";
import { CourseSourceList } from "./course-source-list";
import { StudyDocument } from "../../types";
import { CanvasCourse } from "@/lib/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { AutoCollapseSidebar } from "./auto-collapse-sidebar";
import { useNotebookContext } from "../../hooks/useNotebook";

interface SidebarLeftProps extends React.ComponentProps<typeof Sidebar> {
  courses: CanvasCourse[];
  notebooks: Notebook[];
  selectedNotebook: Notebook;
  onNotebookChange: (notebook: Notebook) => void;
  sources: StudyDocument[];
  selectedSources: StudyDocument[];
  openedSource?: StudyDocument;
  onSourceToggle: (document: StudyDocument) => void;
  openUploadModal: () => void;
  openLanding: () => void;
  enterCreateFlow: () => void;
  onSourceOpen?: (document: StudyDocument) => void;
  onSourceRemove?: (document: StudyDocument) => void;
  onSourceRename?: (document: StudyDocument) => void;
}

export function SidebarLeft({
  courses,
  notebooks,
  selectedNotebook,
  onNotebookChange,
  sources,
  selectedSources,
  openedSource,
  onSourceToggle,
  openUploadModal,
  openLanding,
  enterCreateFlow,
  onSourceOpen,
  onSourceRemove,
  onSourceRename,
  ...props
}: SidebarLeftProps) {
  const { currentPage } = useNotebookContext();
  const courseSources = sources.filter((s) => s.source === "course");
  const externalSources = sources.filter((s) => s.source !== "course");

  return (
    <Sidebar side="left" collapsible="icon" className="border-theme" {...props}>
      {/* Top / Notebook context */}
      <AutoCollapseSidebar shouldCollapse={currentPage.type !== "chat"} />

      <SidebarHeader className="border-theme bg-transparent px-3 py-3 space-y-2">
        <NotebookSwitcher
          courses={courses}
          notebooks={notebooks}
          selectedNotebook={selectedNotebook}
          onNotebookChange={onNotebookChange}
          openLanding={openLanding}
          enterCreateFlow={enterCreateFlow}
        />
      </SidebarHeader>

      {/* Sources header */}
      <SidebarHeader className="bg-transparent gap-2 border-theme px-3 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:w-full text-sm font-medium text-theme-0">
            <span className="group-data-[collapsible=icon]:hidden">
              Sources
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-theme-1" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="max-w-xs text-xs">
                    Answers are generated using only your selected sources and
                    include citations.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </SidebarHeader>
      <div className="px-3">
        <Separator className="mb-4 h-px rounded-full" />
      </div>
      <SidebarContent className="px-1 bg-transparent">
        <Button
          onClick={openUploadModal}
          className="
    h-10 group-data-[collapsible=icon]:mx-1 mx-3 gap-1 px-3
    border border-theme text-theme-0
    rounded-full
    hover:bg-theme-2
    transition
  "
        >
          <Plus className="size-4" />

          <span className="group-data-[collapsible=icon]:hidden">
            Add sources
          </span>
        </Button>

        {/* Source lists */}

        <CourseSourceList
          title="Course Sources"
          sources={courseSources}
          openedSource={openedSource}
          selectedSources={selectedSources}
          onSourceToggle={onSourceToggle}
          isCourse
          onSourceOpen={onSourceOpen}
          onSourceRemove={onSourceRemove}
          onSourceRename={onSourceRename}
        />

        <CourseSourceList
          title="External Sources"
          sources={externalSources}
          openedSource={openedSource}
          selectedSources={selectedSources}
          onSourceToggle={onSourceToggle}
          onSourceOpen={onSourceOpen}
          onSourceRemove={onSourceRemove}
          onSourceRename={onSourceRename}
        />
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}
